import React, { useEffect } from 'react';
import { setPageTitle } from '../../../utils/title';
import Breadcrumb from '../../../components/Breadcrumb';
import './MLAIPlatform.scss';
import infrastructureDiagram from '../../../assets/images/case-studies/case_study_2.png';

const MLAIPlatform = () => {
  useEffect(() => {
    setPageTitle('Case Study - ML/AI Platform');
  }, []);

  const breadcrumbItems = [
    { path: '/', label: 'Home' },
    { path: '/case-studies', label: 'Case Studies' },
    { path: '/case-studies/ml-ai-platform', label: 'ML/AI Platform' },
  ];

  return (
    <div className="case-study-page">
      <Breadcrumb items={breadcrumbItems} />
      <div className="case-study">
        <div className="case-study__header">
          <h1>
            Scalable Cloud Infrastructure Supporting ML/AI Pipelines for a Bio-Mechanical Technology
            Startup, with a Focus on Information Security
          </h1>
          <p className="case-study__preamble">
            DevSecOps Inc. assisted Sparta Science, a bio-mechanical ML startup specializing in
            improving human musculoskeletal health, reducing injuries, and significantly shortening
            recovery times through providing actionable insights from a variety of human health
            metrics sourced from multiple data points. We implemented a scalable, secure, and highly
            available cloud infrastructure based on Amazon ECS, with RDS integration, auto-scaling
            groups, Secrets Manager, serverless data and ML training pipelines, AWS Cognito
            integration, and managed OpenSearch for both SIEM and app document storage.
            Additionally, we utilized AWS GuardDuty and Security Hub for intelligent threat
            detection and compliance monitoring.
          </p>
        </div>

        <div className="case-study__content">
          <section className="case-study__section">
            <h2>Problem Statement</h2>
            <p>
              The customer had been refining their injury risk management solution for many years,
              developing machine learning algorithms to help athletes and military personnel avoid
              injuries and ensure quick recovery, significantly reducing their return-to-duty time.
              By the time we met them, they were already serving a great number of happy customers.
              The missing component was a scalable cloud infrastructure to support the execution of
              their customer-facing application, the Movement Health Platform (MHP), and machine
              learning model training pipelines. Additionally, serving MHP to military personnel and
              healthcare facilities required the implementation of rigorous security controls. The
              absence of a scalable and secure cloud infrastructure was an obstacle to the company's
              growth and prevented them from entering regulated markets.
            </p>
          </section>

          <section className="case-study__section">
            <h2>Implemented Solution</h2>
            <p>
              To address these challenges and meet our client's needs for scalability and security,
              we developed a cloud infrastructure based on Amazon ECS. This infrastructure provided
              scalable platform services and underlying virtual computing resources, and was
              carefully protected from external threats through the thoughtful selection and design
              of security components.
            </p>

            <p>To ensure successful implementation, we first performed the following activities:</p>
            <ul>
              <li>
                Cloud Infrastructure <strong>Audit</strong>
              </li>
              <li>
                DevOps and DevSecOps <strong>integration strategy</strong>
              </li>
            </ul>

            <p>The architecture of the proposed solution included the following components:</p>
            <ul>
              <li>
                <strong>Amazon ECS</strong> with{' '}
                <strong>Auto-Scaling Group of EC2 instances</strong> – for the platform's
                application services
              </li>
              <li>
                <strong>Amazon ECS on Fargate</strong> – for the execution of scheduled and
                on-demand data pipelines
              </li>
              <li>
                <strong>Amazon RDS Aurora for PostgreSQL</strong> – a scalable and resilient
                database cluster
              </li>
              <li>
                <strong>EC2 Image Builder</strong> pipelines – for automatically producing the
                hardened (secure) base AMIs and container images on a predefined schedule, thus
                meeting the regular security patching requirement
              </li>
              <li>
                <strong>Amazon Cognito</strong> – as the application-level Identity Provider and a
                point of extensibility for integration with customer's SSO solutions and IdP's
              </li>
              <li>
                <strong>Amazon OpenSearch</strong> – as a managed application-level document
                storage. Separate instance of OpenSearch was installed to act as a SIEM solution for
                the platform
              </li>
              <li>
                <strong>AWS Secrets Manager, SSM Parameter Store</strong> – for storing
                application-level secrets and environment variables
              </li>
              <li>
                <strong>Amazon CloudWatch</strong> with OpenSearch subscriptions – as a temporary
                storage for all account-level application and service logs, and as a source of data
                for CloudWatch Alarms
              </li>
              <li>
                <strong>AWS Security Hub</strong> – for compliance monitoring (SOC 2, HIPAA)
              </li>
              <li>
                <strong>AWS GuardDuty</strong> – for intelligent threat monitoring
              </li>
              <li>
                <strong>CI/CD pipelines</strong> for all application and data science (ML)
                components built on GitHub actions; with deployment capability to 5 different
                environments – development, staging, and 3 production environments
              </li>
            </ul>

            <p>
              The infrastructure was created using the IaC ("Infrastructure as Code") approach
              following the best Secure SDLC practices, and was provisioned to 3 different AWS
              regions to ensure low latency access for Sparta Science customers: United States
              (North Virginia), United States (San Francisco), Australia (Sydney).
            </p>

            <p>
              This infrastructure allowed engineering team to ensure successful implementation of
              applications and data science components. Additionally, our client enjoyed the ability
              to easy spin up / shut down new environments of such complex infrastructure in
              different geographic regions for their customers, for both demonstration and
              production purposes.
            </p>

            <h2>Solution Architecture</h2>
            <div className="case-study__diagram">
              <img src={infrastructureDiagram} alt="ML/AI Platform Infrastructure Diagram" />
              <p className="diagram-caption">ML/AI Platform Infrastructure Architecture Diagram</p>
            </div>
          </section>

          <section className="case-study__section">
            <h2>Outcomes</h2>
            <ul>
              <li>Scalable and Resilient Cloud Infrastructure Architecture</li>
              <li>High Availability: 95%</li>
              <li>Efficient Multi-environment CI/CD pipelines</li>
              <li>Operational Observability and Alerting</li>
              <li>Strong Security Posture</li>
              <li>Well-defined RTO/RPO</li>
              <li>Portable Cloud Infrastructure (IaC, Terraform)</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MLAIPlatform;
